/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, VStack } from "@chakra-ui/react";
import { FunctionComponent, ReactElement, useEffect, useMemo, useState } from "react";
import { generatePath, useLocation, Location, useNavigate } from "react-router-dom";

// Components
import { RouterPathMapping } from "../../../Router";
import SectionHeader from "../../../shared/DataCardHeader";
import DataCardDisplay, { DataItem } from "../../../shared/DataDisplay/DataDisplay";
import DataCardDisplayDocs from "../../../shared/DataDisplayDocs/DataCardDisplayDocs";
import DataCardDisplayStatement from "../../../shared/DataDisplayStatement";
import StickyFooter from "../../../shared/StickyFooter/StickyFooter";
import StepperFooter from "../../../shared/StickyFooter/footers/StepperFooter";

// Context
import { useClaim } from "../../../context-providers/claims-provider";

// Hooks
import { useAuth } from "../../../hooks/useAuth";
import useClaimsToast from "../../../hooks/useToast";

// Utils
import { BillingType, EnvKeys, UserRole, footerStyles } from "../../../utils/consts";
import { createExternalLinkFromEnv, transformClaimToDisplayStatementData } from "../../../utils/helpers";
import { ClaimServiceBillingType } from "../../../utils/types";

interface ConfirmationProps {
	isAttorney: boolean;
	/**
	 * Delegate to handle logic when User clicks the Previous button
	 * @returns void
	 */
	handleStepPrevious: () => void;
	handleClaimSubmit: (receivedOn: Date | undefined) => Promise<void>;
	handleEditStep: (index: number) => void;
}

const Confirmation: FunctionComponent<ConfirmationProps> = (props: ConfirmationProps): ReactElement => {
	const { isAttorney, handleStepPrevious, handleClaimSubmit, handleEditStep } = props;
	const { claim, claimCase, localClaim, handleClaimUpsert, handleFetchCase, getServiceLabel, localBenefits } =
		useClaim();
	const toast = useClaimsToast();
	const navigate = useNavigate();
	const { hasRole } = useAuth();
	const { state } = useLocation() as Location<{ from: string }>;
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleSubmitting = async (): Promise<void> => {
		setIsSubmitting(true);
		// If an attorney, set the receivedOn date to the current date before submitting
		let receivedOn = isAttorney ? new Date() : undefined;
		handleClaimSubmit(receivedOn)
			.then(() => {
				toast("Success", "Successfully submitted!", "success");
				const claimsDetail = generatePath(
					hasRole([UserRole.Provider]) ? RouterPathMapping.Attorney_ClaimDetail : RouterPathMapping.ClaimDetail,
					{ id: claim.claimId?.toString() ?? null }
				);
				navigate(claimsDetail, { state: state });
			})
			.finally(() => setIsSubmitting(false));
	};

	const dateFormatted = (date: any) => {
		const d = new Date(date);
		return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
	};

	// Helper function to get legal benefit by ID
	const getLegalBenefit = (id: number) => {
		return localBenefits.find((benefitItem) => benefitItem.id === id);
	};

	const getClaimInformation = (): Array<DataItem> => {
		let returnArray: Array<DataItem> = [
			{
				title: "PASS Case",
				value: `#${claimCase?.getCaseid()}`,
				isurl: true,
				url: createExternalLinkFromEnv(EnvKeys.PassUrl, claimCase?.getCaseid() || "")
			},
			{
				title: "LAMP Case",
				value: `#${claimCase?.getLampcaseid()}`,
				isurl: true,
				url: createExternalLinkFromEnv(EnvKeys.LampCaseUrl, claimCase?.getLampcaseid() || "")
			},
			{
				title: "Member",
				value: `${claim.member?.getName()} #${claim.member?.getId()}`,
				isurl: true,
				url: createExternalLinkFromEnv(EnvKeys.MemberPassUrl, claim.member?.getId() || "")
			},
			{
				title: "Attorney",
				value: `${claim.atty?.getName()} #${claim.atty?.getId()}`,
				isurl: true,
				url: createExternalLinkFromEnv(EnvKeys.LampAttorneyUrl, claim.atty?.getId() || "")
			},
			{ title: "Date of Initial Consultation", value: dateFormatted(claim.dateOfInitialConsultation) },
			{ title: "Final Date of Service", value: dateFormatted(claim.finalDateOfService) }
		];

		if (!hasRole([UserRole.Provider])) {
			returnArray.push({ title: "Received On", value: dateFormatted(claim.receivedOn) });
		}

		return returnArray;
	};

	// Determine if there are any High Fixed Value services using the same logic as billing step
	const hasHighFixedValueServices = useMemo(() => {
		return claim.claimServices.some((service) => {
			const benefit = service.legalBenefitId ? getLegalBenefit(service.legalBenefitId) : undefined;
			let hasHourly = service.billing?.some(
				(billingItem) =>
					billingItem.type &&
					[ClaimServiceBillingType.HourlyAttorney, ClaimServiceBillingType.HourlyParalegal].includes(billingItem.type)
			);
			return benefit?.billingType === BillingType.FixedHighValue && !hasHourly;
		});
	}, [claim.claimServices, localBenefits]);

	useEffect(() => {
		if (!claimCase && claim && claim.caseId) {
			handleFetchCase(claim.caseId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [claimCase, claim]);

	// Transform claim data and add High Fixed Value service detection
	const displayStatementData = useMemo(() => {
		const baseData = transformClaimToDisplayStatementData(claim, getServiceLabel);
		return {
			...baseData,
			data: {
				...baseData.data,
				hasHighFixedValueServices: hasHighFixedValueServices
			}
		};
	}, [claim, getServiceLabel, hasHighFixedValueServices]);

	return (
		<>
			<VStack width="100%" spacing={5} alignItems="flex-start">
				<Box width="100%">
					<SectionHeader title="Claim Information" onEdit={() => handleEditStep(0)} />
					<DataCardDisplay data={getClaimInformation()} leftLabelMinWidth={100} rightLabelMinWidth={210} />
				</Box>

				<Box width="100%">
					<SectionHeader title="Services & Billing" onEdit={() => handleEditStep(1)} />
					<DataCardDisplayStatement data={displayStatementData} />
				</Box>

				<Box width="100%" mb={5}>
					<SectionHeader title="Documents" onEdit={() => handleEditStep(3)} />
					<DataCardDisplayDocs isPermanentDeleteEnabled={true} isProvider={hasRole([UserRole.Provider])} />
				</Box>
			</VStack>
			<StickyFooter normalStyles={footerStyles} stickyStyles={footerStyles}>
				<StepperFooter
					hasPreviousStep={true}
					hasNextStep={false}
					isNextDisabled={false}
					isSubmit={true}
					onSubmit={() => handleSubmitting()}
					isSubmitLoading={isSubmitting}
					onPrevious={() => handleStepPrevious()}
				/>
			</StickyFooter>
		</>
	);
};

export default Confirmation;
